import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Text from 'components/shared/Text';
import { calculateStrength } from 'validation/auth.validation';
import styles from './passwordStrength.module.css';

const PasswordStrength = ({ width, value }) => {
  const [strength, setStrength] = useState(-1);

  useEffect(() => {
    setStrength(calculateStrength(value));
  }, [value]);

  // Map strength levels to UI display:
  // -1: No password (no dots)
  // 0: Less than 6 chars (1 red dot)
  // 1: 6+ chars but no special requirements (1 red dot)
  // 2: 6+ chars + 1 requirement (2 orange dots)
  // 3: 6+ chars + 2 requirements (3 orange dots)
  // 4: 6+ chars + 3 requirements (all 4 green dots)
  const getLevel = index => {
    if (strength === -1) return ''; // No password entered

    // For 4-dot UI
    if (strength === 0 || strength === 1) {
      // Less than 6 chars or 6+ chars but no special requirements - only first dot is red
      return index === 0 ? 'red' : '';
    } else if (strength === 2) {
      // 6+ chars + 1 requirement - first two dots are orange
      return index < 2 ? 'orange' : '';
    } else if (strength === 3) {
      // 6+ chars + 2 requirements - first three dots are orange
      return index < 3 ? 'orange' : '';
    } else if (strength === 4) {
      // 6+ chars + all requirements - all dots are green
      return 'green';
    }

    return '';
  };

  // Get text color based on strength
  const getTextClass = () => {
    if (strength === 0 || strength === 1) return styles.redText;
    if (strength < 4) return styles.orangeText;
    return styles.greenText;
  };

  // Get strength text
  const getStrengthText = () => {
    if (strength === -1) return '';
    if (strength === 0 || strength === 1) return 'WEAK'; // Less than 6 chars or 6+ chars but no special requirements
    if (strength < 4) return 'MEDIUM'; // 6+ chars with some requirements
    return 'STRONG'; // All requirements met
  };

  return (
    <div style={{ width }} className={styles.wrapper}>
      <div>
        <Text type="s2" className={styles.text}>
          PASSWORD STRENGTH:
        </Text>
        {strength >= 0 && (
          <Text type="s3" className={getTextClass()}>
            {getStrengthText()}
          </Text>
        )}
      </div>
      <div className={styles.blocksWrapper}>
        {[1, 2, 3, 4].map((el, i) => (
          <div key={i} className={`${styles.block} ${styles[getLevel(i)]}`} />
        ))}
      </div>
    </div>
  );
};

PasswordStrength.propTypes = {
  width: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

PasswordStrength.defaultProps = {};

export default PasswordStrength;
